import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import config from '../../config.json';

const apiUrl = config.apiUrl;

const LeaveUpdate = () => {
    const [leaveData, setLeaveData] = useState({
        TransactionID: '',
        TransactionDate: '',
        EmployeeID: '',
        DateStart: '',
        DateEnd: '',
        LeaveType: '',
        Remarks: '',
    });

    const LeaveTypes = config.LeaveTypes;

    const [employeeList, setEmployeeList] = useState([]);
    const [leaveTypeList, setLeaveTypeList] = useState([]);

    const { leaveId } = useParams();
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isDisable, setIsDisable] = useState(false);
    const navigate = useNavigate();
    const roles = JSON.parse(localStorage.getItem('Roles'));

    const userAccess = {
        hasView: roles?.includes('View') || false,
        hasAdmin: roles?.includes('Admin') || false,
        hasApprover: roles?.includes('Approver') || false,
        hasStandard: roles?.includes('Standard') || false,
    };

    const { hasView, hasAdmin, hasStandard, hasApprover } = userAccess;

    const handleInputChange = (e) => {
        const value = e.target.name === 'LeaveType' ? parseInt(e.target.value, 10) : e.target.value;
        setLeaveData({ ...leaveData, [e.target.name]: value });
    };

    // Function to calculate the duration in decimal format between DateEnd and DateStart
    const calculateDuration = () => {
        const startDate = new Date(leaveData.DateStart);
        const endDate = new Date(leaveData.DateEnd);
        const durationInMinutes = (endDate - startDate) / (1000 * 60);

        // Convert duration to hours and minutes in decimal format
        const hours = Math.floor(durationInMinutes / 60);
        const minutes = durationInMinutes % 60;
        const durationInDecimal = hours + minutes / 60;

        return isNaN(durationInDecimal) ? 0 : durationInDecimal.toFixed(2);
    };

    useEffect(() => {
        const formattedDateTime = (dateTimeString) => {
            const dateObject = new Date(dateTimeString);
            const year = dateObject.getFullYear();
            const month = String(dateObject.getMonth() + 1).padStart(2, '0');
            const day = String(dateObject.getDate()).padStart(2, '0');
            const hours = String(dateObject.getHours()).padStart(2, '0');
            const minutes = String(dateObject.getMinutes()).padStart(2, '0');

            return `${year}-${month}-${day}T${hours}:${minutes}`;
        };

        const fetchData = async () => {
            if (leaveId) {
                setIsDisable(true);
                try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${apiUrl}/Leave/${leaveId}`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                    });

                    if (!response.ok) {
                        setErrorMessage('Failed to fetch leave data');
                        setSuccessMessage('');
                        return;
                    }

                    const data = await response.json();
                    setLeaveData({
                        TransactionID: data.Details.TransactionID,
                        TransactionDate: formattedDateTime(data.Details.TransactionDate),
                        EmployeeID: data.Details.Employee.EmployeeID,
                        DateStart: formattedDateTime(data.Details.DateStart),
                        DateEnd: formattedDateTime(data.Details.DateEnd),
                        LeaveType: data.Details.LeaveType,
                        Remarks: data.Details.Remarks,
                    });

                    setIsDisable(false);
                } catch (error) {
                    setErrorMessage('Error fetching leave data');
                    setSuccessMessage('');
                    setIsDisable(false);
                }
            }
        };

        const fetchEmployeeList = async () => {
            setIsDisable(true);
            const token = localStorage.getItem('token');
            try {
                const response = await fetch(`${apiUrl}/Employee/Namelist`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    setErrorMessage('Failed to fetch employee data');
                    setSuccessMessage('');
                    return;
                }

                const data = await response.json();
                setEmployeeList(data.Employee);
                setIsDisable(false);
            } catch (error) {
                setErrorMessage('Error fetching employee data');
                setSuccessMessage('');
                setIsDisable(false);
            }
        };

        const setLeaveTypes = () => {
            const leaveTypes = Object.keys(LeaveTypes).map(key => ({
                id: LeaveTypes[key],
                name: key,
            }));
            setLeaveTypeList(leaveTypes);
        };

        fetchData();
        fetchEmployeeList();
        setLeaveTypes();
    }, []);

    const handleForUpdate = async (e) => {
        e.preventDefault();
        const { EmployeeID, DateStart, DateEnd, LeaveType, Remarks } = leaveData;

        const updateLeave = {
            EmployeeID,
            DateStart,
            DateEnd,
            LeaveType: LeaveTypes[LeaveType],
            Remarks,
        };

        const duration = calculateDuration(DateStart, DateEnd);
        if (duration <= 0) {
            setErrorMessage('DateStart must be earlier than DateEnd');
            setSuccessMessage('');
            return;
        }

        // Disable the button when the API process starts
        setIsDisable(true);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${apiUrl}/Leave/Update/${leaveId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(updateLeave),
            });

            if (response.ok) {
                const data = await response.json();
                setErrorMessage('');
                setSuccessMessage(data.Message);
                setIsDisable(false);
                navigate('/leave-list');
            }
        } catch (error) {
            setErrorMessage('Error updating leave');
            setSuccessMessage('');
            setIsDisable(false);
        }
    };

    const {
        TransactionID,
        TransactionDate,
        EmployeeID,
        DateStart,
        DateEnd,
        LeaveType,
        Remarks,
    } = leaveData;

    return (
        <div>
            {(userAccess.hasAdmin || userAccess.hasStandard) ? (
                <>
                    <h3>Update Leave</h3>
                    {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                    {successMessage && <div className="alert alert-success">{successMessage}</div>}
                    <form onSubmit={handleForUpdate}>
                        <div className='mb-3'>
                            <label htmlFor='transactionID' className='form-label'>Transaction Number</label>
                            <input
                                type="text"
                                className="form-control custom-width"
                                id="transactionID"
                                name="TransactionID"
                                value={TransactionID}
                                readOnly={true}
                            />
                        </div>
                        <div className='mb-3'>
                            <label htmlFor='transactionDate' className='form-label'>Transaction Date</label>
                            <input
                                type="datetime-local"
                                className="form-control custom-width"
                                id="transactionDate"
                                name="TransactionDate"
                                value={TransactionDate}
                                readOnly={true}
                            />
                        </div>
                        <div className='mb-3'>
                            <label htmlFor='employeeID' className='form-label'>Employee</label>
                            <select
                                type="text"
                                className="form-control custom-width"
                                id="employeeID"
                                name="EmployeeID"
                                value={EmployeeID}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="">---Select an employee---</option>
                                {employeeList.map(employee => (
                                    <option key={employee.EmployeeID} value={employee.EmployeeID}>
                                        {`${employee.Name}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className='mb-3'>
                            <label htmlFor='leaveType' className='form-label'>Leave Type</label>
                            <select
                                type="text"
                                className="form-control custom-width"
                                id="leaveType"
                                name="LeaveType"
                                value={LeaveTypes[LeaveType]}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="">---Select a leave type---</option>
                                {leaveTypeList.map(leaveType => (
                                    <option key={leaveType.id} value={leaveType.id}>
                                        {leaveType.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className='mb-3'>
                            <label htmlFor='dateStart' className='form-label'>Date Start</label>
                            <input
                                type="datetime-local"
                                className="form-control custom-width"
                                id="dateStart"
                                name="DateStart"
                                value={DateStart}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="dateEnd" className="form-label">Date End</label>
                            <input
                                type="datetime-local"
                                className="form-control custom-width"
                                id="dateEnd"
                                name="DateEnd"
                                value={DateEnd}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className='mb-3'>
                            <label htmlFor='duration' className='form-label'>Duration (hours)</label>
                            <input
                                type="text"
                                className="form-control custom-width"
                                id="duration"
                                name="duration"
                                value={calculateDuration()}
                                readOnly={true}
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="remarks" className="form-label">
                                Remarks
                            </label>
                            <input
                                type="text"
                                className="form-control custom-width"
                                id="remarks"
                                name="Remarks"
                                value={Remarks}
                                onChange={handleInputChange}
                                maxLength={500}
                                required
                            />
                        </div>
                        <Link to="/leave-list" className="btn btn-secondary me-2">Back</Link>
                        {(userAccess.hasStandard || userAccess.hasAdmin) && (
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={isDisable}
                            >
                                Update
                            </button>
                        )}
                    </form>
                </>
            ) : (
                <div>
                    <h3>No access!</h3>
                    <button className="btn btn-primary" onClick={() => navigate(-1)}>
                        Go Back
                    </button>
                </div>
            )}
        </div>
    );
};

export default LeaveUpdate;
